import { Box, Chip } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  Briefcase,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Heart,
  MapPin,
  Plane,
  Share2,
  Stethoscope,
  Users
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import authService from "../../api/authService";
import jobSearchApi from "../../api/jobSearch";
import professionalProfileApi from "../../api/professionalProfile";
import subscriptionApi from "../../api/subscription";
import Button from "../../components/common/Button";
import useSubscriptionLimits from "../../hooks/useSubscriptionLimits";
import { useAuthStore } from "../../store/authStore.js";
import { titleCase } from "../../utils/helpers";

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const {
    data: subscriptionLimits,
    error: subscriptionLimitsError,
    isError: isSubscriptionLimitsError,
    isLoading: isSubscriptionLimitsLoading,
    refetch: refetchSubscriptionLimits,
  } = useSubscriptionLimits();

  const { data: mySubscription, error: mySubscriptionError } = useQuery({
    queryKey: ["mySubscription"],
    queryFn: async () => {
      const res = await subscriptionApi.getMySubscription();
      return res.data;
    },
    enabled: !!user,
  });

  const {data: verificationData, error: verificationError, isLoading: isLoadingVerificationData} = useQuery({
    queryKey: ['professionalVerificationStatus'],
    queryFn: async () => {
      const res = await professionalProfileApi.getVerificationStatus();
      return res.data;
    }
  })


  if (verificationError) {
    Sentry.captureException(
      verificationError,
      "Error while fetching company verification status",
    )
  }

  if (mySubscriptionError) {
    Sentry.captureException(
      mySubscriptionError,
      "Error while fetching my subscriptions",
    );
  }
  const { has_subscription } = mySubscription || {};
  const isFullyVerified = verificationData?.is_verified;
  const canApplyForJob = subscriptionLimits?.limits.job_applications?.can_apply;
  // const requiresSubscription = subscriptionLimits?.subscription === null;
  const requiresSubscription = !has_subscription;
  const remainingApplications =
    subscriptionLimits?.limits.job_applications?.remaining;

  const fetchJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobSearchApi.getJobDetails(jobId);
      setJob(response.data);
    } catch (err) {
      setError("Failed to load job details. Please try again.");
      Sentry.captureException(err, "Job details fetch error");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const checkIsSaved = useCallback(async () => {
    if (!user) return;
    try {
      const response = await authService.getSavedSearches();
      const savedItem = response.data.find(
        (item) => item.query === `job_id:${jobId}`,
      );
      if (savedItem) {
        setIsSaved(true);
        setSavedId(savedItem.id);
      }
    } catch (error) {
      // Silently fail, as this is not critical functionality
      Sentry.captureException(error, "Check saved job error");
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobDetails();
    checkIsSaved();
  }, [fetchJobDetails, checkIsSaved]);

  const handleSaveToggle = async () => {
    if (!user) {
      navigate("/signin");
      return;
    }
    if (isSaved) {
      try {
        await authService.unsaveJob(savedId);
        setIsSaved(false);
        setSavedId(null);
      } catch (error) {
        // handle error silently for now
        Sentry.captureException(error, "Unsave job error");
      }
    } else {
      try {
        const response = await authService.saveJob({
          id: job.id,
          title: job.title,
        });
        setIsSaved(true);
        setSavedId(response.data.id);
      } catch (error) {
        // handle error silently
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-600 border-t-transparent absolute inset-0"></div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-160px)]">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg border">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-red-600 mb-6 text-lg">
            {error || "Job not found."}
          </p>
          <Button onClick={() => navigate("/job-search")} variant="primary">
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  // const { title, company_name, location, salary_range, job_type, experience_level, description, responsibilities, qualifications, created_at, days_ago, days_until_expiry } = job;
  const {
    title,
    company_name,
    company_website,
    recruiter_name,
    location,
    salary_range,
    salary_min,
    salary_max,
    job_type,
    experience_level,
    department,
    aircraft_type,
    description,
    responsibilities,
    qualifications,
    benefits,
    license_requirements,
    total_flying_hours_required,
    specific_aircraft_hours_required,
    medical_certification_required,
    contact_email,
    application_url,
    is_remote,
    is_urgent,
    created_at,
    updated_at,
    expiry_date,
    expected_start_date,
    days_ago,
    days_until_expiry,
    id,
    status,
    visibility,
    pilot_type,
  } = job;

  const aviationRequirements = [
    { label: "Aircraft Type", value: aircraft_type, icon: Plane },
    { label: "License Required", value: license_requirements, icon: Award },
    {
      label: "Total Flying Hours",
      value:
        total_flying_hours_required &&
        `${total_flying_hours_required || 0} hrs`,
      icon: Clock,
    },
    {
      label: "Aircraft Hours",
      value:
        specific_aircraft_hours_required &&
        `${specific_aircraft_hours_required || 0} hrs`,
      icon: Plane,
    },
    {
      label: "Medical Certificate",
      value: medical_certification_required,
      icon: Stethoscope,
    },
  ];

  const jobMetadata = [
    {
      label: "Posted",
      value: `${days_ago} day${days_ago > 1 ? "s" : ""} ago`,
      icon: Calendar,
    },
    {
      label: "Expires In",
      value: days_until_expiry
        ? `${days_until_expiry} day${days_until_expiry > 1 ? "s" : ""}`
        : "N/A",
      icon: Clock,
    },
    { label: "Pilot Type", value: titleCase(pilot_type), icon: Plane },
    { label: "Department", value: titleCase(department), icon: Building },
    {
      label: "Expected Start",
      value: expected_start_date || "TBD",
      icon: Calendar,
    },
  ];

  const isJobExpired = Boolean(
    job?.is_expired ||
    (job?.expiry_date && new Date(job.expiry_date) < new Date()) ||
    job?.status === 'expired'
  );

  const handleJobApplication = (jobId) => {
    if (!user) {
      navigate("/signin");
      return;
    }
    if (isJobExpired) {
      Swal.fire({
        title: "Job Expired",
        text: "This job posting has expired and is no longer accepting applications.",
        icon: "warning",
        confirmButtonText: "Browse Available Jobs",
        confirmButtonColor: "#0284c7",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/job-search");
        }
      });
      return;
    }
    if (!isFullyVerified){
      Swal.fire({
        title: "Profile Not Verified",
        text: "You need a complete and verified profile before applying for jobs.",
        icon: "warning",
        confirmButtonText: "Go to Profile",
        cancelButtonText: "Cancel",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          return navigate('/manage-profile')
        }
      })
    }
    else if (requiresSubscription) {
      Swal.fire({
        title: "Subscription Required",
        text: "You need an active subscription to apply for jobs. Would you like to view our pricing plans?",
        icon: "info",
        confirmButtonText: "View Pricing",
        cancelButtonText: "Cancel",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/pricing')
        }
      })
      // return
      // toast.info("An active subscription is required to apply for jobs.");
      // return navigate("/pricing");
    }
    else{

      navigate(`/job-application/${jobId}`);
    }
  };

  return (
    <div className="bg-secondary-50">
      <div className="container mx-auto py-8 px-4">
        <Button
          variant="link"
          onClick={() => navigate("/job-search")}
          className="mb-6 text-secondary-600 hover:text-primary-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Jobs
        </Button>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <main className="lg:col-span-8 space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {job?.cover_image ? (
                <div className="h-52 w-full">
                  <img
                    src={job.cover_image}
                    className="w-full h-full object-cover"
                    alt="Job cover"
                  />
                </div>
              ) : (
                <div className="h-3 w-full bg-gradient-to-r from-primary-400 to-primary-600" />
              )}
              <div className="p-6 sm:p-8">
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center font-bold text-primary-600 text-2xl flex-shrink-0">
                      {has_subscription ? company_name?.charAt(0) : "AC"}
                    </div>
                    <div>
                      <p className="text-primary-600 font-semibold">
                        {has_subscription ? company_name : "Aviation Company"}
                      </p>
                      <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">
                        {title}
                      </h1>
                    </div>
                  </div>
                  {is_urgent && (
                    <Chip
                      color="error"
                      label="Urgent Hiring"
                      className="animate-pulse"
                    />
                  )}
                </Box>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-secondary-600 text-sm mt-6 pt-6 border-t border-gray-100">
                  <span className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1.5 text-primary-500" />{" "}
                    {location}
                  </span>
                  <span className="flex items-center">
                    <Briefcase className="w-4 h-4 mr-1.5 text-primary-500" />{" "}
                    {job_type}
                  </span>
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1.5 text-primary-500" />{" "}
                    {experience_level}
                  </span>
                  <span className="flex items-center font-semibold text-green-600">
                    <DollarSign className="w-4 h-4 mr-1.5 text-green-500" />{" "}
                    {salary_range}
                  </span>
                  {/* {is_remote && (
                  <span className="flex items-center">
                    <Chip label={is_remote && "Remote"} color="info" />
                  </span>
                )} */}
                </div>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-gray-100">
              <Section title="Job Description">
                <p className="text-secondary-700 leading-relaxed prose">
                  <ReactMarkdown>{description}</ReactMarkdown>
                </p>
              </Section>
              <Section title="Key Responsibilities">
                <p className="prose">
                  <ReactMarkdown>{responsibilities}</ReactMarkdown>
                </p>
                {/* <ul className="space-y-3">
                  {responsibilities
                    ?.split("\n")
                    .map(
                      (item, i) =>
                        item.trim() && <ListItem key={i}>{item}</ListItem>
                    )}
                </ul> */}
              </Section>
              <Section title="Qualifications & Requirements">
                <div className="mt-6">
                  {/* <h4 className="font-semibold text-secondary-800 mb-4 text-lg">
                        Aviation Specific Requirements
                      </h4> */}
                  <div className="grid gap-3">
                    {aviationRequirements.map(
                      (req, index) =>
                        req.value && (
                          <div
                            key={index}
                            className="flex items-center justify-between py-3 px-4 bg-secondary-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <req.icon className="w-4 h-4 text-primary-600" />
                              <span className="text-secondary-600 font-medium">
                                {req.label}
                              </span>
                            </div>
                            <span className="font-semibold text-secondary-800">
                              {req.value}
                            </span>
                          </div>
                        ),
                    )}
                  </div>
                </div>
                {/* <ul className="space-y-3">
                  {qualifications
                    ?.split("\n")
                    .map(
                      (item, i) =>
                        item.trim() && <ListItem key={i}>{item}</ListItem>
                    )}
                </ul> */}
              </Section>
              {benefits && (
                <>
                  {/* <Divider className="my-8" /> */}
                  <Section title="Benefits & Perks">
                    <div className="prose prose-lg max-w-none">
                      <ReactMarkdown>{benefits}</ReactMarkdown>
                    </div>
                    {/* <ul className="space-y-3">
                      {benefits
                        ?.split("\n")
                        .map(
                          (item, i) =>
                            item.trim() && <ListItem key={i}>{item}</ListItem>
                        )}
                    </ul> */}
                  </Section>
                </>
              )}
            </div>
          </main>

          <aside className="lg:col-span-4 space-y-6 sticky top-24">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-start gap-3">
                <div>
                  <Button
                    size="lg"
                    onClick={() => handleJobApplication(job.id)}
                    className="flex-1"
                    disabled={isJobExpired || !canApplyForJob}
                  >
                    {isJobExpired
                      ? "Job Expired"
                      : canApplyForJob
                      ? "Apply Now"
                      : "Application Limit Reached"}
                  </Button>
                  {remainingApplications >= 0 && (
                    <div className="text-sm text-gray-500">
                      {remainingApplications} applications remaining
                    </div>
                  )}
                </div>
                <Button
                  variant={isSaved ? "primary" : "secondary"}
                  size="lg"
                  className="p-3"
                  onClick={handleSaveToggle}
                >
                  <Heart
                    className={`w-5 h-5 ${
                      isSaved ? "fill-current text-white" : ""
                    }`}
                  />
                </Button>
                <Button variant="secondary" size="lg" className="p-3">
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="font-bold text-lg mb-4">Job Information</h3>
              <div className="space-y-4">
                {jobMetadata.map(
                  (info, index) =>
                    info.value &&
                    info.value !== "N/A" && (
                      <div
                        key={index}
                        className="flex items-center justify-between py-2"
                      >
                        <div className="flex items-center gap-3 text-secondary-600">
                          <info.icon className="w-4 h-4" />
                          <span>{info.label}</span>
                        </div>
                        <span className="font-semibold text-secondary-800 text-sm">
                          {info.value}
                        </span>
                      </div>
                    ),
                )}
              </div>
              {/* <ul className="space-y-3 text-sm">
                <InfoRow
                  label="Posted On"
                  value={`${days_ago} day${days_ago > 1 ? "s" : ""} ago`}
                />
                <InfoRow
                  label="Expires In"
                  value={
                    days_until_expiry
                      ? `${days_until_expiry} day${
                          days_until_expiry > 1 ? "s" : ""
                        }`
                      : "N/A"
                  }
                />
                <InfoRow label="Department" value={department || "N/A"} />
                <InfoRow label="Aircraft Type" value={aircraft_type || "N/A"} />
                

                {company_website && (
                  <InfoRow
                    label="Website"
                    value={
                      <a
                        href={company_website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 underline"
                      >
                        {company_website}
                      </a>
                    }
                  />
                )}
              </ul> */}
              {/* Recruiter Info */}
              {/* {recruiter_name && (
                <>
                  <Divider className="my-4" />
                  <div className="space-y-2">
                    <h4 className="font-semibold text-secondary-800 text-sm">
                      Posted By
                    </h4>
                    <p className="text-secondary-600">{recruiter_name}</p>
                    {contact_email && (
                      <p className="text-primary-600 text-sm">
                        {contact_email}
                      </p>
                    )}
                  </div>
                </>
              )} */}
            </div>
            {/* Company Info Card */}
            {mySubscription?.has_subscription && company_website && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h3 className="font-bold text-xl mb-4 text-secondary-900">
                  Company
                </h3>
                <div className="space-y-3">
                  {company_name && (
                    <p className="font-semibold text-secondary-800">
                      {company_name}
                    </p>
                  )}
                  {company_website && (
                    <a
                      href={company_website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-2"
                    >
                      Visit Website →
                    </a>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children, icon: Icon }) => (
  <div className="mb-8 last:mb-0">
    <h1 className="text-2xl font-bold text-secondary-800 mb-4 pb-2 border-b-2 border-primary-100 flex items-center gap-3">
      {Icon && <Icon className="w-6 h-6 text-primary-600" />}
      {title}
    </h1>
    {children}
  </div>
);

const ListItem = ({ children }) => (
  <li className="flex items-start">
    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
    <span className="text-secondary-700">{children}</span>
  </li>
);

const InfoRow = ({ label, value }) => (
  <li className="flex justify-between items-center bg-secondary-50 p-2 rounded-md">
    <span className="text-secondary-600">{label}</span>
    <span className="font-semibold text-secondary-800">{value}</span>
  </li>
);

export default JobDetailsPage;
